import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  MagnifyingGlass, 
  Download, 
  Pencil, 
  Trash, 
  CaretLeft,
  CaretRight,
  X,
  CheckCircle,
  XCircle,
  Exam,
  NotePencil
} from '@phosphor-icons/react';
import { resultsApi, programsApi, formatApiErrorDetail } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const resultSchema = z.object({
  registrationNumber: z.string().min(1, 'Required'),
  programCode: z.string().min(1, 'Required'),
  subjectCode: z.string().min(1, 'Required'),
  subjectName: z.string().optional(),
  internalMarks: z.string().min(1, 'Required'),
  externalMarks: z.string().min(1, 'Required'),
});

export default function ViewResultsPage() {
  const [results, setResults] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [publishFilter, setPublishFilter] = useState('');
  const [editResult, setEditResult] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [bulkAction, setBulkAction] = useState(false);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(resultSchema),
  });

  useEffect(() => { loadPrograms(); }, []);
  useEffect(() => { loadResults(); }, [pagination.page, searchText, programFilter, publishFilter]);

  const loadPrograms = async () => {
    try {
      const { data } = await programsApi.getAll();
      setPrograms(data);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const loadResults = async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit };
      if (searchText.trim()) params.registrationNumber = searchText.trim();
      if (programFilter) params.programCode = programFilter;
      if (publishFilter) params.isPublished = publishFilter === 'true';
      const { data } = await resultsApi.getAll(params);
      setResults(data.data);
      setPagination(prev => ({ ...prev, total: data.total, totalPages: data.totalPages }));
    } catch (error) {
      console.error('Failed to load results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadResults();
  };

  const clearFilters = () => {
    setSearchText('');
    setProgramFilter('');
    setPublishFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExport = async () => {
    try {
      const response = await resultsApi.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'results.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const togglePublish = async (result) => {
    try {
      const { data } = await resultsApi.togglePublish(result.id);
      setResults(prev => prev.map(r => r.id === result.id ? { ...r, isPublished: data.isPublished } : r));
      toast.success(data.isPublished ? 'Result published' : 'Result unpublished');
    } catch (error) {
      toast.error('Failed to update publish status');
    }
  };

  const openEditModal = (result) => {
    setEditResult(result);
    reset(result);
  };

  const handleEdit = async (data) => {
    setSaving(true);
    try {
      await resultsApi.update(editResult.id, data);
      toast.success('Result updated');
      setEditResult(null);
      loadResults();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await resultsApi.delete(deleteResult.id);
      toast.success('Result deleted');
      setDeleteResult(null);
      loadResults();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteAll = async () => {
    try {
      const { data } = await resultsApi.deleteAll();
      toast.success(data?.message || 'All results deleted');
      setShowDeleteAll(false);
      loadResults();
    } catch (error) {
      toast.error('Failed to delete all results');
    }
  };

  const handleBulkPublish = async (publish) => {
    setBulkAction(true);
    try {
      const { data } = await resultsApi.bulkPublish(publish);
      toast.success(data?.message || `${publish ? 'Published' : 'Unpublished'} all results`);
      loadResults();
    } catch (error) {
      toast.error('Bulk publish failed');
    } finally {
      setBulkAction(false);
    }
  };

  const handleRecalculate = async () => {
    setBulkAction(true);
    try {
      const { data } = await resultsApi.recalculate();
      toast.success(data?.message || 'Results recalculated');
      loadResults();
    } catch (error) {
      toast.error('Recalculation failed');
    } finally {
      setBulkAction(false);
    }
  };

  const hasFilters = searchText || programFilter || publishFilter;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="overline mb-2">Result Management</p>
          <h1 className="text-3xl font-black tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            View Results
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => handleBulkPublish(true)} disabled={bulkAction} variant="outline" data-testid="publish-all-btn"
            className="rounded-none border-green-600 text-green-700 hover:bg-green-50 h-9 text-sm px-3">
            <CheckCircle size={16} className="mr-1.5" />
            Publish All
          </Button>
          <Button onClick={() => handleBulkPublish(false)} disabled={bulkAction} variant="outline" data-testid="unpublish-all-btn"
            className="rounded-none border-orange-500 text-orange-600 hover:bg-orange-50 h-9 text-sm px-3">
            <XCircle size={16} className="mr-1.5" />
            Unpublish All
          </Button>
          <Button onClick={handleRecalculate} disabled={bulkAction} variant="outline" data-testid="recalculate-btn"
            className="rounded-none border-blue-500 text-blue-600 hover:bg-blue-50 h-9 text-sm px-3">
            Recalculate
          </Button>
          <Button onClick={() => setShowDeleteAll(true)} variant="outline" data-testid="delete-all-results-btn"
            className="rounded-none border-[#E31E25] text-[#E31E25] hover:bg-[#FEF2F2] h-9 text-sm px-3">
            <Trash size={16} className="mr-1.5" />
            Delete All
          </Button>
          <Button onClick={handleExport} data-testid="export-results-btn" className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none h-9 text-sm px-3">
            <Download size={16} className="mr-1.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="result-search-form">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <Input value={searchText} onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by Registration Number..." className="pl-10 rounded-none border-[#E8E8E8] h-10"
            data-testid="result-search-input" />
        </div>
        <Select value={programFilter} onValueChange={(val) => { setProgramFilter(val); setPagination(prev => ({ ...prev, page: 1 })); }}>
          <SelectTrigger className="rounded-none border-[#E8E8E8] w-full sm:w-[200px] h-10" data-testid="result-program-filter">
            <SelectValue placeholder="All Programs" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-white border-[#E8E8E8] max-h-60">
            {programs.map((p) => (
              <SelectItem key={p.programCode} value={p.programCode}>{p.programCode}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={publishFilter} onValueChange={(val) => { setPublishFilter(val); setPagination(prev => ({ ...prev, page: 1 })); }}>
          <SelectTrigger className="rounded-none border-[#E8E8E8] w-full sm:w-[160px] h-10" data-testid="result-publish-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-white border-[#E8E8E8]">
            <SelectItem value="true">Published</SelectItem>
            <SelectItem value="false">Unpublished</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none h-10 px-6" data-testid="result-search-btn">
          Search
        </Button>
        {hasFilters && (
          <Button type="button" variant="outline" onClick={clearFilters} className="rounded-none h-10 px-4" data-testid="result-clear-filters-btn">
            <X size={16} className="mr-1" /> Clear
          </Button>
        )}
      </form>

      {/* Table */}
      <div className="bg-white border border-[#E8E8E8] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="results-table">
            <thead>
              <tr className="bg-[#FEF7F7] border-b border-[#E8E8E8]">
                <th className="text-left py-3 px-4 text-sm font-semibold text-[#1A1A1A]">Reg. No.</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-[#1A1A1A]">Subject</th>
                <th className="text-right py-3 px-3 text-sm font-semibold text-[#1A1A1A]">Int.</th>
                <th className="text-right py-3 px-3 text-sm font-semibold text-[#1A1A1A]">Ext.</th>
                <th className="text-right py-3 px-3 text-sm font-semibold text-[#1A1A1A]">Total</th>
                <th className="text-center py-3 px-3 text-sm font-semibold text-[#1A1A1A]">Status</th>
                <th className="text-center py-3 px-3 text-sm font-semibold text-[#1A1A1A]">Published</th>
                <th className="text-center py-3 px-3 text-sm font-semibold text-[#1A1A1A]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-[#E8E8E8]">
                    {[...Array(8)].map((_, j) => (
                      <td key={j} className="py-3 px-4"><div className="h-4 bg-[#E8E8E8] animate-pulse w-16" /></td>
                    ))}
                  </tr>
                ))
              ) : results.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-[#999]">
                    <Exam size={40} className="mx-auto mb-3 text-[#ccc]" />
                    <p className="font-medium text-[#666]">No results found</p>
                    <p className="text-sm mt-1">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : (
                results.map((result) => (
                  <tr key={result.id} className="border-b border-[#E8E8E8] hover:bg-[#FAFAFA] transition-colors">
                    <td className="py-3 px-4">
                      <div className="text-sm font-mono font-medium text-[#333]">{result.registrationNumber}</div>
                      <div className="text-xs text-[#999] mt-0.5">{result.programCode}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium text-[#333]">{result.subjectCode}</div>
                      {result.subjectName && <div className="text-xs text-[#999] mt-0.5 max-w-[200px] truncate">{result.subjectName}</div>}
                    </td>
                    <td className="py-3 px-3 text-sm text-right font-medium text-[#333]">
                      {result.internalMarks === 'AB' ? <span className="text-orange-600 font-semibold">AB</span> :
                       result.internalMarks === 'NF' ? <span className="text-purple-600 font-semibold">NF</span> :
                       result.internalMarks}
                    </td>
                    <td className="py-3 px-3 text-sm text-right font-medium text-[#333]">
                      {result.externalMarks === 'AB' ? <span className="text-orange-600 font-semibold">AB</span> :
                       result.externalMarks === 'NF' ? <span className="text-purple-600 font-semibold">NF</span> :
                       result.externalMarks}
                    </td>
                    <td className="py-3 px-3 text-sm text-right font-bold text-[#1A1A1A]">{result.totalMarks}</td>
                    <td className="py-3 px-3 text-center">
                      {result.resultStatus === 'Pass' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                          <CheckCircle size={12} weight="bold" /> Pass
                        </span>
                      ) : result.resultStatus === 'Absent' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                          Absent
                        </span>
                      ) : result.resultStatus === 'Not Found' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                          Not Found
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                          <XCircle size={12} weight="bold" /> Fail
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Switch checked={result.isPublished} onCheckedChange={() => togglePublish(result)}
                        data-testid={`publish-toggle-${result.id}`} />
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEditModal(result)} data-testid={`edit-result-${result.id}`}
                          className="p-2 hover:bg-[#E31E25]/10 text-[#999] hover:text-[#E31E25] transition-colors rounded">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => setDeleteResult(result)} data-testid={`delete-result-${result.id}`}
                          className="p-2 hover:bg-[#E31E25]/10 text-[#999] hover:text-[#E31E25] transition-colors rounded">
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-[#E8E8E8] bg-[#FAFAFA]">
            <p className="text-sm text-[#666]">
              {pagination.total > 0 ? `Showing ${((pagination.page - 1) * pagination.limit) + 1}-${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}` : '0 results'}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} className="rounded-none h-8 w-8 p-0">
                <CaretLeft size={16} />
              </Button>
              <span className="text-sm font-medium text-[#333] min-w-[60px] text-center">
                {pagination.page} / {pagination.totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={pagination.page === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} className="rounded-none h-8 w-8 p-0">
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Result Modal */}
      <Dialog open={!!editResult} onOpenChange={() => setEditResult(null)}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 bg-white border-0 shadow-2xl rounded-none overflow-hidden" data-testid="edit-result-modal">
          {/* Modal Header */}
          <div className="bg-[#E31E25] px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-white text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                <NotePencil size={22} weight="bold" />
                Edit Result
              </DialogTitle>
              <p className="text-white/70 text-sm mt-1">
                {editResult?.registrationNumber} - {editResult?.subjectCode}
              </p>
            </DialogHeader>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit(handleEdit)} className="p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Registration No.</Label>
                <Input {...register('registrationNumber')}
                  className="mt-1.5 h-10 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25] font-mono text-sm"
                  data-testid="edit-result-reg-input" />
                {errors.registrationNumber && <p className="text-[#E31E25] text-xs mt-1">{errors.registrationNumber.message}</p>}
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Program</Label>
                <Select defaultValue={editResult?.programCode} onValueChange={(value) => setValue('programCode', value)}>
                  <SelectTrigger className="mt-1.5 h-10 rounded-none border-[#E0E0E0] bg-[#FAFAFA]" data-testid="edit-result-program-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-white border-[#E0E0E0] max-h-60">
                    {programs.map((p) => (
                      <SelectItem key={p.programCode} value={p.programCode}>{p.programCode} - {p.programName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Subject Code</Label>
                <Input {...register('subjectCode')}
                  className="mt-1.5 h-10 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25] font-mono text-sm"
                  data-testid="edit-result-subject-code-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Subject Name</Label>
                <Input {...register('subjectName')}
                  className="mt-1.5 h-10 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25] text-sm"
                  data-testid="edit-result-subject-name-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Internal Marks</Label>
                <Input {...register('internalMarks')} placeholder="e.g. 25, AB, NF"
                  className="mt-1.5 h-10 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25] text-center font-bold"
                  data-testid="edit-result-internal-input" />
                {errors.internalMarks && <p className="text-[#E31E25] text-xs mt-1">{errors.internalMarks.message}</p>}
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">External Marks</Label>
                <Input {...register('externalMarks')} placeholder="e.g. 45, AB, NF"
                  className="mt-1.5 h-10 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25] text-center font-bold"
                  data-testid="edit-result-external-input" />
                {errors.externalMarks && <p className="text-[#E31E25] text-xs mt-1">{errors.externalMarks.message}</p>}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#F0F0F0]">
              <Button type="button" variant="outline" onClick={() => setEditResult(null)}
                className="rounded-none border-[#E0E0E0] hover:bg-[#F5F5F5] px-6 h-10" data-testid="edit-result-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={saving}
                className="bg-[#E31E25] hover:bg-[#C41A20] text-white rounded-none px-6 h-10" data-testid="edit-result-save-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteResult} onOpenChange={() => setDeleteResult(null)}>
        <AlertDialogContent className="rounded-none border-0 shadow-2xl p-0 overflow-hidden" data-testid="delete-result-dialog">
          <div className="bg-[#FEF2F2] px-6 py-4 border-b border-[#FCA5A5]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#991B1B] font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Delete Result
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#B91C1C] text-sm">
                Delete result for <strong>{deleteResult?.registrationNumber}</strong> - {deleteResult?.subjectCode}? This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="px-6 py-4">
            <AlertDialogCancel className="rounded-none" data-testid="delete-result-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}
              className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none" data-testid="delete-result-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Results Confirmation */}
      <AlertDialog open={showDeleteAll} onOpenChange={setShowDeleteAll}>
        <AlertDialogContent className="rounded-none border-0 shadow-2xl p-0 overflow-hidden" data-testid="delete-all-results-dialog">
          <div className="bg-[#FEF2F2] px-6 py-4 border-b border-[#FCA5A5]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#991B1B] font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Delete All Results
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#B91C1C] text-sm">
                This will permanently delete <strong>all {pagination.total} results</strong> from the database. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="px-6 py-4">
            <AlertDialogCancel className="rounded-none" data-testid="delete-all-results-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAll}
              className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none" data-testid="delete-all-results-confirm-btn">
              Delete All Results
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
